import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import {
  createInitialFlowState,
  InventoryFlowAction,
  inventoryFlowReducer,
  InventoryFlowState,
} from "contexts/inventoryFlowReducer";
import type { MemberIdentifierType } from "domains/inventory/services/types";

const STORAGE_KEY = "inventory_flow_v1";

type InventoryFlowContextValue = {
  state: InventoryFlowState;
  dispatch: React.Dispatch<InventoryFlowAction>;
  mergeState: (partial: Partial<InventoryFlowState>) => void;
  startRental: () => void;
  setIdentity: (payload: {
    contact: string | null;
    memberId: string | null;
    identifierType: MemberIdentifierType | null;
    identifier: string | null;
  }) => void;
  selectItem: (payload: { item: InventoryFlowState["item"]; inventoryId: string }) => void;
  createOrderSuccess: (payload: {
    orderId?: string;
    code?: number;
    lockerDoorId?: string;
    returnMethod?: string;
  }) => void;
  enterCodeSuccess: (payload: {
    mode: InventoryFlowState["mode"];
    step: InventoryFlowState["step"];
    code?: number;
    codeStatus?: "active" | "used";
    codeType?: InventoryFlowState["codeType"];
    orderId?: string;
    inventoryId?: string;
    lockerDoorId?: string;
    doorId?: string;
    returnMethod?: string;
  }) => void;
  openDoorResult: (payload: { doorId?: string; doorOpenFailed?: boolean }) => void;
  reportUsageSuccess: () => void;
  setError: (error: InventoryFlowState["error"]) => void;
  clearFlow: () => void;
};

export const InventoryFlowContext = createContext<InventoryFlowContextValue | null>(null);

const loadState = (): InventoryFlowState => {
  if (typeof sessionStorage === "undefined") return createInitialFlowState();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return createInitialFlowState();
    const parsed = JSON.parse(raw) as Partial<InventoryFlowState>;
    if (!parsed.step || !parsed.mode || !parsed.flowId) {
      return createInitialFlowState();
    }
    return {
      ...createInitialFlowState(),
      ...parsed,
      updatedAt: parsed.updatedAt ?? new Date().toISOString(),
    };
  } catch (error) {
    return createInitialFlowState();
  }
};

export function InventoryFlowProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(inventoryFlowReducer, undefined, loadState);

  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    const isIdle = state.step === "idle" && !state.error && !state.code && !state.orderId;
    if (isIdle) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const mergeState = useCallback(
    (partial: Partial<InventoryFlowState>) => {
      dispatch({ type: "MERGE_STATE", payload: partial });
    },
    [dispatch]
  );

  const startRental = useCallback(() => {
    dispatch({ type: "START_RENTAL" });
  }, []);

  const setIdentity = useCallback(
    (payload: {
      contact: string | null;
      memberId: string | null;
      identifierType: MemberIdentifierType | null;
      identifier: string | null;
    }) => {
      dispatch({ type: "SET_IDENTITY", payload });
    },
    []
  );

  const selectItem = useCallback(
    (payload: { item: InventoryFlowState["item"]; inventoryId: string }) => {
      if (!payload.item) return;
      dispatch({
        type: "SELECT_ITEM",
        payload: { item: payload.item, inventoryId: payload.inventoryId },
      });
    },
    []
  );

  const createOrderSuccess = useCallback(
    (payload: {
      orderId?: string;
      code?: number;
      lockerDoorId?: string;
      returnMethod?: string;
    }) => {
      dispatch({ type: "CREATE_ORDER_SUCCESS", payload });
    },
    []
  );

  const enterCodeSuccess = useCallback(
    (payload: {
      mode: InventoryFlowState["mode"];
      step: InventoryFlowState["step"];
      code?: number;
      codeStatus?: "active" | "used";
      codeType?: InventoryFlowState["codeType"];
      orderId?: string;
      inventoryId?: string;
      lockerDoorId?: string;
      doorId?: string;
      returnMethod?: string;
    }) => {
      dispatch({ type: "ENTER_CODE_SUCCESS", payload });
    },
    []
  );

  const openDoorResult = useCallback((payload: { doorId?: string; doorOpenFailed?: boolean }) => {
    dispatch({ type: "OPEN_DOOR_RESULT", payload });
  }, []);

  const reportUsageSuccess = useCallback(() => {
    dispatch({ type: "REPORT_USAGE_SUCCESS" });
  }, []);

  const setError = useCallback((error: InventoryFlowState["error"]) => {
    if (!error) return;
    dispatch({ type: "SET_ERROR", payload: { error } });
  }, []);

  const clearFlow = useCallback(() => {
    dispatch({ type: "CLEAR_FLOW" });
  }, []);

  const value = useMemo(
    () => ({
      state,
      dispatch,
      mergeState,
      startRental,
      setIdentity,
      selectItem,
      createOrderSuccess,
      enterCodeSuccess,
      openDoorResult,
      reportUsageSuccess,
      setError,
      clearFlow,
    }),
    [
      state,
      dispatch,
      mergeState,
      startRental,
      setIdentity,
      selectItem,
      createOrderSuccess,
      enterCodeSuccess,
      openDoorResult,
      reportUsageSuccess,
      setError,
      clearFlow,
    ]
  );

  return <InventoryFlowContext.Provider value={value}>{children}</InventoryFlowContext.Provider>;
}

export function useInventoryFlow() {
  const context = useContext(InventoryFlowContext);
  if (!context) {
    throw new Error("useInventoryFlow must be used within InventoryFlowProvider");
  }
  return context;
}
